/*----------------------------------
- FOOTER: THE NIGHT LAUNCH PICTURE
----------------------------------*/

/**
 * The world footer's picture on every page: the brand's night render of the station launching its rocket (Home v3 and
 * the page template of 2026-11-04, `docs/mockups/website-home-v3/footer-night-1672x941.webp`). One cut for every width:
 * the mock draws it at the card's full width and its natural height (`.wf-scene img`), on phones too, so there is no
 * phone cut to crop. The recipe of commit 06bad0cd5 (`homeScenes.ts`): ffmpeg crops the mock's file to 1672 x 940 (its
 * last row dropped: Chromium decodes an odd-height AVIF from `sips` as a fully transparent picture), `sips` writes the
 * AVIF and a Chromium canvas the WebP fallback (this machine's ffmpeg has no WebP encoder).
 *
 * @docs docs/features/website-navigation-shell
 * @adr  ADR-0110
 * @rule The footer picture is lazy on every page (`WorldPicture` priority `lazy`): it sits far below the fold.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { WorldPictureSources } from '@uniquedomains/components/marketing/hero-scene/WorldPicture';
import FooterNightWebp from '@/client/assets/world/footer-night-1672x940.webp';
import FooterNightPhoneWebp from '@/client/assets/world/footer-night-828x466.webp';

/*----------------------------------
- CONSTANTS
----------------------------------*/

// Why the AVIF is a public URL and not an import: Proteum's asset rule does not cover `.avif` (homeScenes.ts says
// more). `?v=` marks it versioned for Proteum's public cache: bump it whenever the file is exported again, since its
// name does not change. Encoded with `sips -s format avif -s formatOptions 60` (81 KB, against 112 KB for the WebP at quality 0.8),
// the quality that lands near the hero station's AVIF weight per pixel.
const FOOTER_AVIF_VERSION = '20261104';

/** The night launch picture, 1672 x 940 (the delivered 941 less its last row). */
export const WORLD_FOOTER_NIGHT_SCENE: WorldPictureSources = {
    desktop: {
        avif: `/public/assets/world/footer-night-1672x940.avif?v=${FOOTER_AVIF_VERSION}`,
        webp: FooterNightWebp,
        width: 1672,
        height: 940,
    },
    // The same frame scaled to 828 wide (466 high, even sides) for phones, from 900px down: 27 KB AVIF and 35 KB WebP in
    // place of the 81 and 112 KB desktop cut (R5-W3 W3-7). The footer draws the whole picture at every width, so the
    // phone cut is the desktop one scaled, never cropped.
    phone: {
        avif: `/public/assets/world/footer-night-828x466.avif?v=${FOOTER_AVIF_VERSION}`,
        webp: FooterNightPhoneWebp,
        width: 828,
        height: 466,
    },
};
